import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bold, Italic, Strikethrough, Underline } from 'lucide-react'
import type { ReactNode } from 'react'
import type { FloatingFormatToolbarItem } from '@/components/editor/FloatingToolbar'
import { useCommentMutations } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Button } from '@/components/ui/button'
import { TiptapRichTextEditor } from '@/components/editor/TiptapRichTextEditor'
import { useMentionUsers } from '@/components/editor/MentionExtension'
import { extractPlainTextFromTiptap } from '@/routes/features/tasks/utils/editorUtils'

const COMMENT_TOOLBAR_ITEMS: Array<FloatingFormatToolbarItem> = [
  { format: 'bold', label: 'Bold', Icon: Bold },
  { format: 'italic', label: 'Italic', Icon: Italic },
  { format: 'underline', label: 'Underline', Icon: Underline },
  { format: 'strikethrough', label: 'Strike', Icon: Strikethrough },
]

const COMMENT_SLASH_COMMANDS = [
  'bulleted-list',
  'numbered-list',
  'code-block',
  'blockquote',
]

interface CommentEditorProps {
  entityType: string
  entityId: string
  parentCommentId?: string
  anchorId?: string | null
  onCancel?: () => void
  onSuccess?: () => void
  placeholder?: string
  /** Replaces the default "Post" label (e.g. "Reply" in the notifications pane). */
  submitLabel?: string
  /** Rendered just before the submit button, e.g. a keyboard hint. */
  trailing?: ReactNode
}

export function CommentEditor({
  entityType,
  entityId,
  parentCommentId,
  anchorId,
  onCancel,
  onSuccess,
  placeholder = 'Write a comment...',
  submitLabel,
  trailing,
}: CommentEditorProps) {
  const { t } = useTranslation('common')
  const currentUserId = useLoginStore((state) => state.userId)
  const { createComment } = useCommentMutations()

  // Populate the mention extension's user store so @mention autocomplete works
  useMentionUsers()

  const contentRef = useRef<string>('')
  const [hasContent, setHasContent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editorKey, setEditorKey] = useState(0)

  const handleContentChange = useCallback((content: string) => {
    contentRef.current = content
    const plainText = extractPlainTextFromTiptap(content)
    setHasContent(plainText.trim().length > 0)
    setError(null)
  }, [])

  const handleSubmit = async () => {
    if (!currentUserId) {
      return
    }

    const content = contentRef.current
    const plainText = extractPlainTextFromTiptap(content)

    if (!plainText.trim()) {
      setError('Comment cannot be empty')
      return
    }

    if (plainText.length > 2000) {
      setError('Comment must be 2000 characters or less')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await createComment({
        entityType,
        entityId,
        parentCommentId: parentCommentId ?? null,
        anchorId: anchorId ?? null,
        content,
        contentFormat: 'tiptap',
        contentPlainText: plainText,
      })

      // Reset editor by changing key
      contentRef.current = ''
      setHasContent(false)
      setEditorKey((prev) => prev + 1)
      onSuccess?.()
    } catch (err) {
      setError(t('comments.postFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    contentRef.current = ''
    setHasContent(false)
    setError(null)
    setEditorKey((prev) => prev + 1)
    onCancel?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div data-testid="comment-editor" className="space-y-1.5">
      {/* One compact box with Post inside it (design 6a). */}
      <div
        className="flex items-end gap-2 rounded-lg border bg-card py-1 pl-3 pr-1 focus-within:border-ring/60"
        onKeyDown={handleKeyDown}
      >
        <div className="min-w-0 flex-1">
          <TiptapRichTextEditor
            key={editorKey}
            namespace={`comment-editor-${entityId}-${parentCommentId || 'root'}`}
            editable={!isSubmitting}
            placeholder={placeholder}
            contentEditableClassName="min-h-[28px] py-1"
            placeholderClassName="top-1 left-0"
            onContentChange={handleContentChange}
            floatingToolbar={true}
            toolbarItems={COMMENT_TOOLBAR_ITEMS}
            enableSlashCommands={true}
            allowedSlashCommands={COMMENT_SLASH_COMMANDS}
            enableMentions={true}
            ariaLabel="Comment editor"
          />
        </div>
        {onCancel && hasContent && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t('comments.cancel')}
          </Button>
        )}
        {trailing}
        <Button
          data-testid="comment-submit"
          size="sm"
          className="h-7 px-3 text-xs"
          title={t('comments.shortcutHint')}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('comments.posting') : (submitLabel ?? t('comments.post'))}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
